/**
 *  @file       request-validity.js
 *  @author     Ryan Rossiter, ryan@kingsds.network
 *              Ryan Saweczko, ryansaweczko@distributive.network
 *  @date       January 2020
 *              January 2024
 */
'use strict';

const { validityStampCache } = require('./validity-stamp-cache');
const { DCPError }           = require('dcp/common/dcp-error');
const { assert }             = require('dcp/common/dcp-assert');

if (true) // eslint-disable-line no-constant-condition
{
  /**
   * Method of Request
   *
   * This examines the payload's validity object and the Connection's validity stamp cache
   * configuration.
   *
   * @param {Connection.Request} req
   * @returns {DCPError|false} false if the message is valid, instance of Error otherwise. When returning Error,
   *                    the code property will be set:
   * EDUP:        this request has already been seen; eg possibly part of a replay attack
   * EEXPIRED:    this request is too old to process, our cache doesn't go back that far
   * ETIMETRAVEL: this request claims to be from the future
   * EINVAL:      this request is not correctly formatted.
   */
  exports.validityError = function validityError()
  {
    const validity = this.payload?.validity;

    if (!validity?.stamp || typeof validity.time !== 'number')
      return new DCPError(`invalid validity in request ${this.id}`, 'EINVAL');
    else
    {
      const options = this.connection.options;
      const slop = options.validitySlopTime;
      assert(typeof slop === 'number');

      // determine ttl of the request
      let ttl;
      if (!validity.ttl || typeof validity.ttl !== 'number')
        ttl = options.ttl.default || options.ttl.min;
      else
      {
        if (validity.ttl > options.ttl.max)
          ttl = options.ttl.max;
        else if (validity.ttl < options.ttl.min)
          ttl = options.ttl.min;
        else
          ttl = validity.ttl;
      }

      const now = this.connection.currentTime();
      const minimumPresent = now - slop;
      const maximumPresent = now + slop;
      const expirationTime = validity.time + ttl;

      if (validity.time > maximumPresent)
        return new DCPError(`Request was sent from the future (${validity.time} > ${maximumPresent})`,'ETIMETRAVEL');
      else if (expirationTime < minimumPresent)
        return new DCPError(`Request validity has expired (${expirationTime} < ${minimumPresent})`, 'EEXPIRED', {
          timestamp: validity.time,
          offset: now - validity.time,
        });

      if (validityStampCache.insert(validity.stamp, expirationTime) !== true)
        return new DCPError(`Duplicate request validity stamp: ${validity.stamp}`,'EDUP');
    }

    return false;
  }
}
